import { Stack, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { AppStoreDownloadIcon, GooglePlayDownloadIcon } from 'assets/icons';
import Config from 'config';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  APP_STORE_URL,
  PLAY_GOOGLE_URL,
  STORAGE_INVITATION_EMAIL_EVENT_TRACKING,
} from 'config/constants';
import { useLocation } from 'react-router-dom';
import Api from 'api/api';
import { useEffect } from 'react';
import { TrackEventRequest } from 'api/interfaces/request';
import {
  TrackEventCategoryEnum,
  TrackEventTypeEnum,
} from 'config/TrackEventType';
import { Card } from './styled';
import { messages } from './messages';

export const InstructionsPage = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const theme = useTheme();

  const isDarkMode = theme.palette.type === 'dark';
  const iconColorTheme = isDarkMode ? 'dm' : 'lm';
  const hasRun = sessionStorage.getItem(
    STORAGE_INVITATION_EMAIL_EVENT_TRACKING,
  );

  const queryParams = new URLSearchParams(location.search);

  const code = queryParams.get('code');
  const src = queryParams.get('src');
  const action = queryParams.get('action');

  const step1 = `${
    Config.get().s3PublicBucketUrl
  }emails/app-installation-step-01_${iconColorTheme}.png`;
  const step2 = `${
    Config.get().s3PublicBucketUrl
  }emails/account-creation-step-02_${iconColorTheme}.png`;
  const step3 = `${
    Config.get().s3PublicBucketUrl
  }emails/complete-process-step-03_${iconColorTheme}.png`;

  const redirectToStore = (url: string) => {
    window.location.href = url;
  };

  const trackEvent = async (event: TrackEventRequest) => {
    try {
      await Api.eventTracking(event);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error tracking event:', error);
    }
  };

  const eventType =
    action === 'button'
      ? TrackEventTypeEnum.INCITATION_EMAIL_JOIN_PROGRAM_BUTTON_CLICKED
      : TrackEventTypeEnum.INVITATION_EMAIL_JOIN_PROGRAM_QR_CODE_SCANNER;

  useEffect(() => {
    if (hasRun || !code || !src || !action) return;

    const payload: TrackEventRequest = {
      eventCategory: TrackEventCategoryEnum.INVITATION_EMAIL,
      eventType,
      eventDate: `${new Date().toISOString()}`,
      entityId: code,
      details: {
        src,
        action,
      },
    };

    trackEvent(payload);
    sessionStorage.setItem(STORAGE_INVITATION_EMAIL_EVENT_TRACKING, 'true');

    return () => {
      sessionStorage.removeItem(STORAGE_INVITATION_EMAIL_EVENT_TRACKING);
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Instructions</title>
      </Helmet>

      <Stack
        alignItems="center"
        justifyContent="space-between"
        height={{ xs: 'auto', md: '100%' }}
        sx={{
          width: '100%',
          paddingX: '2.4rem',
          backgroundColor: theme.palette.background.elevated.tetriary,
        }}
      >
        <Stack alignItems="center">
          <Card>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'center', md: 'flex-start' }}
              sx={{ width: '100%' }}
              gap="0.8rem"
            >
              <img
                width="80px"
                height="80px"
                src={step1}
                alt="app-installation-step-01"
              />
              <Stack
                sx={{ marginBottom: '2.4rem' }}
                alignItems={{ xs: 'center', md: 'flex-start' }}
              >
                <Typography
                  variant="h3"
                  component="h3"
                  marginBottom="0.4rem"
                  fontFamily="600"
                  lineHeight="28px"
                  color={theme.palette.text.primary}
                >
                  {t(messages.step1())}
                </Typography>
                <Typography
                  variant="body1"
                  component="p"
                  lineHeight="175%"
                  textAlign={{ xs: 'center', md: 'left' }}
                  color={theme.palette.text.primary}
                >
                  {t(messages.step1Description())}
                </Typography>
              </Stack>
            </Stack>
            <Typography
              variant="body2"
              component="p"
              color={theme.palette.message.text.note}
              align="center"
              lineHeight="22px"
            >
              {t(messages.downloadApp())}
            </Typography>
            <Stack
              alignItems="center"
              justifyContent="center"
              direction="row"
              gap="0.8rem"
              sx={{ marginTop: '0.8rem' }}
            >
              <AppStoreDownloadIcon
                cursor="pointer"
                onClick={() => redirectToStore(APP_STORE_URL)}
              />
              <GooglePlayDownloadIcon
                cursor="pointer"
                onClick={() => redirectToStore(PLAY_GOOGLE_URL)}
              />
            </Stack>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'center', md: 'flex-start' }}
              gap="0.8rem"
              marginTop={{ xs: '4.8rem', md: '5.6rem' }}
            >
              <img
                width="80px"
                height="80px"
                src={step2}
                alt="account-creation-step-02"
              />
              <Stack alignItems={{ xs: 'center', md: 'flex-start' }}>
                <Typography
                  variant="h3"
                  component="h3"
                  marginBottom="0.4rem"
                  lineHeight="28px"
                  textAlign={{ xs: 'center', md: 'left' }}
                  color={theme.palette.text.primary}
                >
                  {t(messages.step2())}
                </Typography>
                <Typography
                  variant="body1"
                  component="p"
                  lineHeight="28px"
                  textAlign={{ xs: 'center', md: 'left' }}
                  color={theme.palette.text.primary}
                >
                  {t(messages.step2Description())}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              gap="0.8rem"
              marginTop={{ xs: '4.8rem', md: '5.6rem' }}
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'center', md: 'flex-start' }}
            >
              <img
                width="80px"
                height="80px"
                src={step3}
                alt="complete-process-step-03"
              />
              <Stack>
                <Typography
                  variant="h3"
                  component="h3"
                  marginBottom="0.4rem"
                  lineHeight="28px"
                  textAlign={{ xs: 'center', md: 'left' }}
                  color={theme.palette.text.primary}
                >
                  {t(messages.step3())}
                </Typography>
                <Typography
                  variant="body1"
                  component="p"
                  lineHeight="28px"
                  textAlign={{ xs: 'center', md: 'left' }}
                  color={theme.palette.text.primary}
                >
                  {t(messages.step3Description())}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </Stack>
    </>
  );
};
